import { useMutation } from 'react-query';
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { BeneficiaryAccount, Business } from '~/types';
import { useBusinessUpdate, useCurrentBusiness } from '~/hooks/businesses';
import featuresWithDefaults from '~/featuresWithDefaults';
import KycModal from '~/pages/kyc/KycModal';
import { useBeneficiariesCreate } from './beneficiaries';
import { applyForKycLevel } from '~/api/kyc';
import useReduxState from './useReduxState';
import { useBeneficiaryAccountsCreate } from './beneficiaryAccounts';

export type KycCheckReturnType = {
  ready: boolean;
  isCompleted: boolean;
  isPending: boolean;
  withKycCheck: <CB extends () => any>(callback: CB) => CB;
};

export const useKycCheck = (): KycCheckReturnType => {
  const currentBusinessKycLevelQuery = useCurrentBusiness();
  const currentBusiness: Business | undefined = currentBusinessKycLevelQuery.data;
  const kycLevel = currentBusinessKycLevelQuery.data?.kycLevel;
  const kycStatus = currentBusinessKycLevelQuery.data?.kycStatus;

  const isCompleted = kycLevel
    ? !featuresWithDefaults.kyc.kycNotCompletedIf(currentBusiness)
    : false;
  const isPending = kycStatus ? featuresWithDefaults.kyc.kycStatusWait.includes(kycStatus) : false;
  const ready = currentBusinessKycLevelQuery.isSuccess;

  const withKycCheck: KycCheckReturnType['withKycCheck'] = (callback) => {
    if (isCompleted) return callback;
    return (() =>
      KycModal.setOpen({
        ready,
        isCompleted,
        isPending,
        withKycCheck,
      })) as any;
  };

  return {
    ready,
    isCompleted,
    isPending,
    withKycCheck,
  };
};

const initialKycPages = [
  {
    pageName: 'companyDetails' as const,
    valid: null,
    disabled: false,
  },
  {
    pageName: 'companyAddress' as const,
    valid: null,
    disabled: false,
  },
  {
    pageName: 'primaryDocument' as const,
    valid: null,
    disabled: false,
  },
  {
    pageName: 'additionalDocuments' as const,
    valid: null,
    disabled: true,
  },
  {
    pageName: 'payoutDetails' as const,
    valid: null,
    disabled: true,
  },
];

type KycPageNames = typeof initialKycPages[0]['pageName'];

type KycPageState = {
  currentPageNumber: number;
  pages: {
    pageName: KycPageNames;
    valid: boolean | null; // null - not checked
    disabled: boolean;
  }[];
};

export const useKycPage = () => {
  const [state, setPageState] = useReduxState<KycPageState>('kycPage', {
    currentPageNumber: 0,
    pages: initialKycPages,
  });

  const goNext = () => {
    setPageState((prev) => {
      return {
        ...prev,
        currentPageNumber: prev.currentPageNumber + 1,
      };
    });
  };
  const goBack = () => {
    setPageState((prev) => {
      return {
        ...prev,
        currentPageNumber: prev.currentPageNumber - 1,
      };
    });
  };
  const goToPage = (pageNumber: number) => {
    setPageState((prev) => {
      return {
        ...prev,
        currentPageNumber: pageNumber,
      };
    });
  };
  const setPageDisabled = (pageName: KycPageNames) => (disabled: boolean) => {
    setPageState((prev) => {
      return {
        ...prev,
        pages: prev.pages.map((item) => {
          if (item.pageName === pageName) {
            return {
              ...item,
              disabled,
            };
          }
          return item;
        }),
      };
    });
  };
  const isDisabled = (pageName: KycPageNames) => {
    return state.pages.find((item) => item.pageName === pageName)?.disabled;
  };

  return {
    state,
    goNext,
    goBack,
    setPageDisabled,
    goToPage,
    isDisabled,
  };
};

export const useBusinessPrimaryDocumentUpdate = () => {
  const currentBusinessQuery = useCurrentBusinessKyc();
  const businessData = currentBusinessQuery.currentBusiness;
  const ready = !!businessData;

  const businessUpdateMutation = useBusinessUpdate();
  const mutate = (primaryDocument: Business['primaryDocument']) => {
    if (businessData) {
      businessUpdateMutation.mutate({
        ...businessData,
        primaryDocument,
      });
    }
    throw new Error('Called before "ready" become true');
  };
  const mutateAsync = (primaryDocument: Business['primaryDocument']) => {
    if (businessData) {
      return businessUpdateMutation.mutateAsync({
        ...businessData,
        ...primaryDocument,
      });
    }
    throw new Error('Called before "ready" become true');
  };

  return {
    ...businessUpdateMutation,
    mutate,
    mutateAsync,
    ready,
  };
};

export const useBusinessAdditionalDocumentsUpdate = () => {
  const currentBusinessQuery = useCurrentBusiness();
  const businessData = currentBusinessQuery.data;
  const ready = !!businessData;

  const businessUpdateMutation = useBusinessUpdate();
  const mutate = (documents: Business['documents']) => {
    if (businessData) {
      businessUpdateMutation.mutate({
        ...businessData,
        documents,
      });
    }
    throw new Error('Called before "ready" become true');
  };
  const mutateAsync = (documents: Business['documents']) => {
    if (businessData) {
      return businessUpdateMutation.mutateAsync({
        ...businessData,
        documents,
      });
    }
    throw new Error('Called before "ready" become true');
  };

  return {
    ...businessUpdateMutation,
    mutate,
    mutateAsync,
    ready,
  };
};

export const useCurrentBusinessKyc = () => {
  const { isLoading, data } = useCurrentBusiness();
  const [currentBusiness, setCurrentBusiness] = useReduxState<Business>('currentBusinessKyc', data);
  return { isLoading, currentBusiness, setCurrentBusiness };
};

/*
export const useAddPayoutDetails = () => {
  const currentBusinessQuery = useCurrentBusiness();
  const createBeneficiaryMutation = useBeneficiariesCreate();
  const allBeneficiariesQuery = useBeneficiariesQuery({
    // create beneficiary if not created
    enabled: !!currentBusinessQuery.data,
    onSuccess(res) {
      const {
        country,
        legalType,
        legalName,
        primaryDocument: { type },
        solutionId,
        id,
      } = currentBusinessQuery.data!;
      if (res.length === 0) {
        createBeneficiaryMutation.mutate({
          country,
          address: {
            country,
          },
          document: {
            legalType,
            legalName,
            type,
          },
          name: legalName!,
          type: 'BUSINESS',
          solutionId,
          profileId: id!,
          nominatedAccounts: [],
        } as any);
      }
    },
  });
  const currentProfileId = auth.getProfile() as string;
  const currentBeneficiary = allBeneficiariesQuery.data?.find(
    (item) => item.profileId === currentProfileId
  );
  // const currentNominatedAccounts = currentBeneficiary?.nominatedAccounts || [];
  const ready = !!currentBeneficiary;

  const beneficiaryCreateMutation = useBeneficiaryAccountsCreate();

  const mutate = (account: BeneficiaryAccount) => {
    if (currentBeneficiary) {
      beneficiaryCreateMutation.mutate({
        ...account,
        solutionId: currentBeneficiary.solutionId,
        profileId: currentBeneficiary.profileId,
        beneficiaryId: currentBeneficiary.id,
      });
    }
    throw new Error('Called before "ready" become true');
  };
  const mutateAsync = (account: BeneficiaryAccount) => {
    if (currentBeneficiary) {
      return beneficiaryCreateMutation.mutateAsync({
        ...account,
        solutionId: currentBeneficiary.solutionId,
        profileId: currentBeneficiary.profileId,
        beneficiaryId: currentBeneficiary.id,
      });
    }
    throw new Error('Called before "ready" become true');
  };

  return {
    ...beneficiaryCreateMutation,
    mutate,
    mutateAsync,
    ready,
  };
};
*/

export const useApplyForKycLevel = () => {
  const currentProfile = useCurrentBusiness();

  const beneficiaryCreateMutation = useBeneficiariesCreate();
  const beneficiaryAccountCreateMutation = useBeneficiaryAccountsCreate();

  const applyForKycLevelMutation = useMutation((payload: any) => applyForKycLevel(payload), {
    onSuccess() {
      void currentProfile.refetch();
    },
  });

  const ready = !!currentProfile;

  const mutate = () => {
    throw new Error('sync mutation is not supported');
  };
  const mutateAsync = (account: BeneficiaryAccount) => {
    if (currentProfile) {
      const { country, legalType, legalName, primaryDocument, solutionId, id } =
        currentProfile.data!;
      const beneficiary = {
        country,
        address: {
          country,
        },
        document: {
          legalType,
          legalName,
          // Send without id to create new document
          ...primaryDocument,
          id: undefined,
        },
        name: legalName!,
        type: 'BUSINESS',
        solutionId,
        profileId: id!,
        nominatedAccounts: [],
      };

      return beneficiaryCreateMutation
        .mutateAsync(beneficiary as any)
        .then((beneficiaryResponse) => {
          return beneficiaryAccountCreateMutation
            .mutateAsync({
              ...account,
              solutionId: beneficiary.solutionId,
              profileId: beneficiary.profileId,
              beneficiaryId: beneficiaryResponse.data.id,
              setAsPrimary: true,
            })
            .then((accountResponse) => {
              return applyForKycLevelMutation.mutateAsync({
                beneficiaryId: accountResponse.data.beneficiaryId,
                beneficiaryAccountId: accountResponse.data.id,
              });
            });
        });
    }
    throw new Error('Called before "ready" become true');
  };
  return {
    ...beneficiaryCreateMutation,
    mutate,
    mutateAsync,
    ready,
  };
};
